import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useState } from "react";
import { toast } from "sonner";
import { Award, BookOpen, Users, Sparkles, Check } from "lucide-react";

const PERKS = [
  { icon: Users, title: "Private Members Directory", body: "Searchable directory of every Association member — cohorts, agencies, expertise." },
  { icon: Award, title: "Annual lapel pin", body: "A cloisonné Foundation pin shipped each year — a small mark of a lifelong commitment." },
  { icon: BookOpen, title: "Foundation briefings", body: "Quarterly briefings on Foundation programs, policy events, and mentor matches." },
  { icon: Sparkles, title: "Founders' badge", body: "A subtle gold badge on your profile and posts across the Association." },
];

export default function Founders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const join = async () => {
    if (!user) { navigate("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/founders/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const active = user && user.founders_member;

  return (
    <div data-testid="founders-page">
      <section className="relative overflow-hidden bg-[#0A192F] text-[#FDFBF7] grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="label-eyebrow text-[#C5A059]">A new tier of membership</div>
            <h1 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
              Founders' <em className="text-[#C5A059] not-italic">Circle</em>.
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
              For Fellows who want to do more than belong. Founders' Circle members fund the Foundation's mentorship programs, unlock the private Members Directory, and receive an annual artifact of their service.
            </p>
          </div>
          <div className="lg:col-span-4">
            <div className="border border-white/15 bg-white/5 backdrop-blur-sm p-8">
              <div className="label-eyebrow text-slate-300">Annual support</div>
              <div className="mt-2 font-display text-6xl text-[#C5A059]">$99<span className="text-2xl text-slate-300">/yr</span></div>
              <div className="mt-2 text-sm text-slate-300">Tax-deductible. Renews on your anniversary.</div>
              {active ? (
                <div className="mt-6 px-4 py-3 rounded-md bg-[#C5A059]/15 border border-[#C5A059]/30 text-[#C5A059] text-sm" data-testid="founders-active">
                  ✓ You are a Founders' Circle member through {new Date(user.founders_until).toLocaleDateString()}.
                </div>
              ) : (
                <Button onClick={join} disabled={busy} className="mt-6 w-full bg-[#C5A059] text-[#0A192F] hover:bg-[#B38F48] rounded-md h-12 font-semibold" data-testid="founders-join-button">
                  {busy ? "Redirecting…" : user ? "Join the Founders' Circle" : "Sign in to join"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-2 gap-6">
          {PERKS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="card-flat p-7 flex gap-5">
              <div className="w-11 h-11 shrink-0 rounded-md bg-[#002855] text-[#C5A059] flex items-center justify-center"><Icon className="w-5 h-5" /></div>
              <div>
                <h3 className="font-display text-2xl text-[#0A192F]">{title}</h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#FDFBF7]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-eyebrow">What your support funds</div>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl text-[#0A192F]">A quiet investment in the next class.</h2>
          <ul className="mt-8 space-y-4 text-slate-700">
            {[
              "Mentor-mentee matching for current Fellows and incoming finalists.",
              "Regional convenings in Washington, San Francisco, and Atlanta.",
              "Need-based travel stipends for Fellows attending Association events.",
              "Archival preservation of Fellows' oral histories — 1977 to today.",
            ].map(t => (
              <li key={t} className="flex gap-3"><Check className="w-5 h-5 text-[#C5A059] shrink-0 mt-0.5" /><span>{t}</span></li>
            ))}
          </ul>
          <div className="mt-12 flex flex-wrap gap-3">
            <Link to="/directory"><Button variant="outline" className="border-[#002855] text-[#002855] hover:bg-[#002855] hover:text-white rounded-md h-11 px-6">Preview the Directory</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
